/**
 * 文件定位：
 * 1. 当前文件位于 Worker 倒角预览函数层；
 * 2. 负责把“当前选中的面”解析为预览气泡引导线锚点；
 * 3. 不参与倒角求解，只服务于 SolidWorks 风格的预览展示语义。
 */

import { resolveFaceShapeByRef } from '../../../common';
import { safeDelete } from '../../functions/chamferPuppyKernel';
import type { ChamferKernelInstance } from '../../types/ChamferShapeBuildInput';

/**
 * 三维点元组。
 */
export type ChamferGuidePoint = [number, number, number];

/**
 * Puppy CAD 内核 曲面适配器最小协议。
 */
interface ChamferGuideSurfaceAdaptor {
  /** 曲面 U 参数下界。 */
  FirstUParameter(): number;
  /** 曲面 U 参数上界。 */
  LastUParameter(): number;
  /** 曲面 V 参数下界。 */
  FirstVParameter(): number;
  /** 曲面 V 参数上界。 */
  LastVParameter(): number;
  /** 计算曲面点和一阶切向。 */
  D1(u: number, v: number, point: unknown, tangentU: unknown, tangentV: unknown): void;
}

/**
 * Puppy CAD 内核 点最小协议。
 */
interface ChamferGuideKernelPoint {
  /** X 坐标。 */
  X(): number;
  /** Y 坐标。 */
  Y(): number;
  /** Z 坐标。 */
  Z(): number;
}

/**
 * 倒角预览面锚点解析输入。
 */
export interface ChamferGuideFaceAnchorInput {
  /** Puppy CAD 内核 实例。 */
  puppy: ChamferKernelInstance;
  /** 源实体 Shape。 */
  sourceShape: unknown;
  /** 源实体拓扑元数据。 */
  sourceMetadata: Record<string, unknown>;
  /** 当前 UI 选中的选择项引用。 */
  preferredItemRef?: string | null;
}

/**
 * 倒角预览面锚点。
 */
export interface ChamferGuideFaceAnchor {
  /** 面选择引用。 */
  sourceRef: string;
  /** 面中心锚点。 */
  anchor: ChamferGuidePoint;
}

/**
 * 判断持久化引用是否为面引用。
 *
 * @param persistentRef 持久化引用。
 * @returns 是否为面引用。
 */
export function isChamferFacePersistentRef(
  persistentRef?: string | null,
): persistentRef is string {
  return typeof persistentRef === 'string' && persistentRef.startsWith('face:');
}

/**
 * 计算面的参数中心点。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param faceShape 面 Shape。
 * @returns 面中心点。
 */
function computeChamferFaceCenterPoint(
  puppy: ChamferKernelInstance,
  faceShape: unknown,
): ChamferGuidePoint {
  let surface: ChamferGuideSurfaceAdaptor | null = null;
  let point: unknown = null;
  let tangentU: unknown = null;
  let tangentV: unknown = null;

  try {
    /**
     * BRepAdaptor_Surface_2 第二个参数为 true 时会限制在修剪面范围内。
     * 因此参数中点更接近 SolidWorks 预览气泡引导线落在“当前选中面中心”的行为。
     */
    surface = new puppy.BRepAdaptor_Surface_2(faceShape as never, true) as ChamferGuideSurfaceAdaptor;
    const uParam = (surface.FirstUParameter() + surface.LastUParameter()) / 2;
    const vParam = (surface.FirstVParameter() + surface.LastVParameter()) / 2;
    point = new puppy.gp_Pnt_3(0, 0, 0);
    tangentU = new puppy.gp_Vec_4(0, 0, 0);
    tangentV = new puppy.gp_Vec_4(0, 0, 0);
    surface.D1(uParam, vParam, point, tangentU, tangentV);

    const resolvedPoint = point as ChamferGuideKernelPoint;
    return [
      resolvedPoint.X(),
      resolvedPoint.Y(),
      resolvedPoint.Z(),
    ];
  } finally {
    safeDelete(tangentV);
    safeDelete(tangentU);
    safeDelete(point);
    safeDelete(surface);
  }
}

/**
 * 解析当前选中面的预览锚点。
 *
 * @param input 面锚点解析输入。
 * @returns 面锚点；当前选中项不是面或无法解析时返回 null。
 */
export function resolvePreferredChamferFaceAnchor(
  input: ChamferGuideFaceAnchorInput,
): ChamferGuideFaceAnchor | null {
  if (!isChamferFacePersistentRef(input.preferredItemRef)) {
    return null;
  }

  try {
    const faceShape = resolveFaceShapeByRef(
      input.puppy,
      input.sourceShape,
      input.sourceMetadata,
      input.preferredItemRef,
    );
    const anchor = computeChamferFaceCenterPoint(input.puppy, faceShape);
    if (anchor.some((coordinate) => !Number.isFinite(coordinate))) {
      return null;
    }
    return {
      sourceRef: input.preferredItemRef,
      anchor,
    };
  } catch {
    /**
     * 当前引用来自交互态选择，可能在预览重建期间失效。
     * 面锚点只是附加展示语义，解析失败时必须退回边锚点，不能反向阻断几何预览。
     */
    return null;
  }
}
